import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';

import { provideNgxWebstorage } from 'ngx-webstorage';
 
// Services
import { AuthService } from '@core/services/auth-new.service';
import { AuthStateService } from '@core/services/auth-state.service';
import { ThemeService } from '@core/services/theme.service';
import { LoadingService } from '@core/services/loading.service';
import { ModalService, ModalConfig } from '@shared/services/modal.service';
import { BrandingService } from '@core/services/branding.service';
import { StorageIntegrityService } from '@core/services/storage-integrity.service';
import { UserStatusRefreshService } from '@core/services/user-status-refresh.service';

// Utilities
import { ThemeMigrationUtil } from '@core/utils/theme-migration.util';

// Shared Components
import { GlobalLoaderComponent } from '@shared/components/global-loader/global-loader.component';
import { LoadingSpinnerComponent } from '@shared/components/loading-spinner/loading-spinner.component';
import { NotificationComponent } from '@shared/components/notification/notification.component';
import { CustomModalComponent } from '@shared/components/custom-modal/custom-modal.component';
import { QuickOptionsModalComponent } from '@shared/components/quick-options-modal/quick-options-modal.component';
import { ToastContainerComponent } from '@shared/components/toast/toast-container.component';
import { StatusChangeModalComponent } from '@features/customers/pages/customer-dashboard/components/status-change-modal/status-change-modal.component';
import { UserStatusModalComponent } from '@features/settings/pages/user-control/components/user-status-modal/user-status-modal.component';
import { UploadManagerComponent } from '@core/upload-manager/upload-manager.component';
import { NavigationLoaderComponent } from '@shared/components/navigation-loader/navigation-loader.component';
import { NoInternetComponent } from '@shared/components/no-internet/no-internet.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    GlobalLoaderComponent,
    LoadingSpinnerComponent,
    NotificationComponent,
    CustomModalComponent,
    QuickOptionsModalComponent,
    ToastContainerComponent,
    StatusChangeModalComponent,
    UserStatusModalComponent,
    UploadManagerComponent,
    NavigationLoaderComponent,
    NoInternetComponent,
  ],

  template: `
    <div class="app-container min-h-screen bg-theme-bg-primary text-theme-text-primary">
      <!-- No Internet Connection Overlay -->
      <app-no-internet></app-no-internet>

      <!-- Global Top Bar Loader (HTTP requests) -->
      <app-global-loader></app-global-loader>

      <!-- Navigation Bottom Bar Loader (route transitions) -->
      <app-navigation-loader></app-navigation-loader>
      
      <!-- Global Loading Spinner -->
      <!-- <app-loading-spinner 
        *ngIf="loadingService.isLoading()" 
        class="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center">
      </app-loading-spinner> -->

      <!-- Global Notifications -->
      <app-notification></app-notification>

      <!-- Toast Notifications -->
      <app-toast-container></app-toast-container>

      <!-- Router Outlet for Layouts -->
      <router-outlet></router-outlet>

      <!-- Global Modals -->
      <app-custom-modal
        *ngFor="let modal of activeModals"
        [isOpen]="true"
        [title]="modal.title"
        [size]="modal.size"
        [closable]="modal.closable !== false"
        [backdropClose]="modal.backdropClose !== false"
        [hasFooterContent]="modal.hasFooterContent || false"
        [hasHeaderContent]="modal.content === 'status-change' || modal.content === 'quick-options' || modal.content === 'user-status-change'"
        (modalClose)="onModalClose(modal.id)"
      >
        <!-- Dynamic Content Based on Modal Type -->
        <app-quick-options-modal 
          *ngIf="modal.content === 'quick-options'"
          (actionSelected)="onQuickOptionsAction($event, modal.id)">
        </app-quick-options-modal>
        
        <!-- Status Change Modal -->
        <app-status-change-modal
          *ngIf="modal.content === 'status-change'"
          [customerData]="modal.data"
          (statusUpdated)="onStatusUpdated(modal.id)"
          (cancelled)="onModalClose(modal.id)">
        </app-status-change-modal>
        <app-user-status-modal
          *ngIf="modal.content === 'user-status-change'"
          [user]="modal.data?.user"
          [onStatusUpdated]="modal.data?.onStatusUpdated"
          (statusUpdated)="onUserStatusUpdated(modal.id, $event)"
          (cancelled)="onModalClose(modal.id)">
        </app-user-status-modal>
        
        <!-- Default content for other modals -->
        <div *ngIf="shouldShowDefaultModalContent(modal)">
          {{ modal.content }}
        </div>
      </app-custom-modal>

      <!-- Removed duplicate modal container to fix z-index stacking issue -->

      <!-- Global Upload Manager Widget (persistent across routes) -->
      <app-upload-manager></app-upload-manager>
    </div>
  `,
  styles: [`
    .app-container {
      display: block;
      height: 100vh;
      overflow: hidden;
    }
  `]
})
    export class AppComponent implements OnInit, OnDestroy {
      title = 'The Operator OS - Operators platform for customer journey  ';
  
  // Services
  authService = inject(AuthService);
  authStateService = inject(AuthStateService);
  themeService = inject(ThemeService);
  loadingService = inject(LoadingService);
  modalService = inject(ModalService);
  brandingService = inject(BrandingService);
  storageIntegrityService = inject(StorageIntegrityService);
  userStatusRefreshService = inject(UserStatusRefreshService);

  // Modal state
  activeModals: ModalConfig[] = [];

  ngOnInit(): void {
    this.initializeApp();
    this.subscribeToModals();
    this.runStartupIntegrityCheck();
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  /** Run storage integrity check once on startup, force-bypass cache. */
  private runStartupIntegrityCheck(): void {
    this.storageIntegrityService.checkIntegrity(true).catch(err => {
      console.error('[App] Startup integrity check threw an unexpected error:', err);
    });
  }

  private initializeApp(): void {
    // ✅ FIXED: Migrate theme preferences before any other theme initialization
    ThemeMigrationUtil.migrateThemePreferences();

    // Initialize company-specific branding (title and favicon)
    this.brandingService.initializeBranding();

    // ThemeService will now automatically initialize with the migrated/correct theme
    // from localStorage with proper key ('tibss_theme') and default to 'light'

    // Verify auth state synchronization between services
    const isAuthStateSynced = this.authStateService.isAuthenticated();
    const isAuthServiceSynced = this.authService.isAuthenticated();
    
    if (isAuthStateSynced !== isAuthServiceSynced) {
      console.warn('Auth services out of sync:', {
        authStateService: isAuthStateSynced,
        authService: isAuthServiceSynced
      });
    }
    
    console.log('🎯 TIBSS Next App Component initialized');
    console.log('🎨 Current theme after migration:', this.themeService.effectiveTheme());
    console.log('🔐 Auth status:', {
      authStateService: isAuthStateSynced,
      authService: isAuthServiceSynced
    });
  }

  private subscribeToModals(): void {
    this.modalService.activeModals$.subscribe(modals => {
      this.activeModals = modals;
    });
  }

  onModalClose(modalId: string): void {
    this.modalService.closeModal(modalId);
  }

  onQuickOptionsAction(action: string, modalId: string): void {
    console.log(`Quick action selected: ${action}`);
    
    switch (action) {
      case 'export':
        console.log('Exporting customer data...');
        break;
      case 'print':
        console.log('Printing customer profile...');
        break;
      case 'share':
        console.log('Sharing customer profile...');
        break;
      case 'bookmark':
        console.log('Bookmarking customer...');
        break;
      case 'edit':
        console.log('Opening quick edit...');
        break;
      case 'history':
        console.log('Opening customer history...');
        break;
      default:
        console.log('Unknown action:', action);
    }
    
    // Close modal after action
    this.modalService.closeModal(modalId);
  }

  onStatusUpdated(modalId: string): void {
    console.log('Status updated successfully');
    this.modalService.closeModal(modalId);
    // You could emit an event here to refresh customer data if needed
  }

  onUserStatusUpdated(modalId: string, status: string): void {
    console.log('User status updated:', status);
    this.modalService.closeModal(modalId);
    this.userStatusRefreshService.notifyStatusChanged();
  }

  shouldShowDefaultModalContent(modal: ModalConfig): boolean {
    if (!modal?.content) {
      return false;
    }

    if (typeof modal.content !== 'string') {
      return true;
    }

    const normalized = modal.content.trim().toLowerCase();
    return !['quick-options', 'status-change', 'user-status-change'].includes(normalized);
  }
}
